import React from 'react';
import { Button } from './ui/button';
import { CheckCircle, ArrowRight, Flag } from 'lucide-react';
import { siteContent } from '../data/mockData';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Hero = ({ onNavigate }) => {
  const content = siteContent.en;
  const [heroRef, heroVisible] = useScrollAnimation();
  const [buttonsRef, buttonsVisible] = useScrollAnimation(200);
  const [trustRef, trustVisible] = useScrollAnimation(400);
  
  const scrollToContact = () => {
    if (onNavigate) {
      onNavigate('contact');
    }
  };

  const handleDownloadCapabilities = () => {
    // Mock download functionality
    alert('Capabilities deck download would start here');
  };

  return (
    <section id="home" className="relative overflow-hidden bg-gradient-to-br from-[#f0f0e8] via-white to-[#f0f0e8]/50 pt-16 pb-16 lg:pt-24 lg:pb-24">
      {/* Background Pattern - Removed circular elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-40 right-20 w-24 h-24 border border-[#c41927] rounded-sm transform rotate-45"></div>
      </div>
      
      <div className="container mx-auto px-4 lg:px-6 relative">
        <div className="max-w-4xl mx-auto text-center">
          {/* Credentials Badge - With Animation */}
          <div ref={heroRef} className={`inline-flex items-center bg-white/80 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-2 mb-8 animate-fade-in-up ${heroVisible ? 'visible' : ''}`}>
            <CheckCircle className="w-4 h-4 text-[#c41927] mr-2" />
            <span className="text-sm font-medium text-gray-700">ISO 13485 Lead Auditors • ISO 9001 • EU MDR • FDA (coming soon)</span>
          </div>
          
          {/* Main Headline - With Animation */}
          <div className={`animate-fade-in-up ${heroVisible ? 'visible' : ''}`} style={{transitionDelay: '0.2s'}}>
            <h1 className="text-4xl lg:text-6xl font-bold text-[#011431] mb-4 leading-tight">
              Leading the way in safety and quality for medical devices, drugs, and biologics
            </h1>
          </div>
          
          {/* Subheading - With Animation */}
          <div className={`animate-fade-in-up ${heroVisible ? 'visible' : ''}`} style={{transitionDelay: '0.4s'}}>
            <p className="text-lg lg:text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Trusted partner for global MedTech companies
            </p>
          </div>
          
          {/* Key Benefits - With Staggered Animation */}
          <div ref={buttonsRef} className={`grid md:grid-cols-3 gap-6 mb-12 max-w-3xl mx-auto animate-fade-in-up ${buttonsVisible ? 'visible' : ''}`} style={{transitionDelay: '0.6s'}}>
            {content.hero.bullets.map((bullet, index) => (
              <div key={index} className={`flex items-center justify-center space-x-2 bg-white/60 backdrop-blur-sm border border-gray-100 rounded-lg px-4 py-3 animate-scale-up ${buttonsVisible ? 'visible' : ''}`} style={{transitionDelay: `${0.8 + index * 0.1}s`}}>
                <CheckCircle className="w-5 h-5 text-[#c41927] flex-shrink-0" />
                <span className="text-gray-700 font-medium text-center">{bullet}</span>
              </div>
            ))}
          </div>
          
          {/* CTA Buttons - With Animation */}
          <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center animate-slide-in-bottom ${buttonsVisible ? 'visible' : ''}`} style={{transitionDelay: '1.2s'}}>
            <Button
              onClick={scrollToContact}
              className="bg-[#c41927] hover:bg-[#a01520] text-white font-semibold px-8 py-3 text-lg group transition-all duration-300 transform hover:scale-105"
            >
              {content.hero.primaryCTA}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            
            <Button
              onClick={handleDownloadCapabilities}
              variant="outline"
              className="border-[#c41927] text-[#c41927] hover:bg-[#c41927] hover:text-white font-semibold px-8 py-3 text-lg transition-all duration-300"
            >
              {content.hero.secondaryCTA}
            </Button>
          </div>

          {/* Trust Indicators - With Staggered Animation */}
          <div ref={trustRef} className={`mt-16 pt-8 border-t border-gray-200 animate-fade-in-up ${trustVisible ? 'visible' : ''}`}>
            <p className="text-sm text-gray-500 mb-4">Trusted across regions</p>
            <div className="flex justify-center items-center space-x-8">
              {[
                { region: 'United States' },
                { region: 'European Union' }, 
                { region: 'MENA Region' }
              ].map((item, index) => (
                <div key={index} className={`flex items-center space-x-2 animate-fade-in-left ${trustVisible ? 'visible' : ''}`} style={{transitionDelay: `${1.6 + index * 0.2}s`}}>
                  <Flag className="w-4 h-4 text-[#c41927]" />
                  <span className="text-sm font-medium text-gray-600">{item.region}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;